import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Box, Typography } from '@mui/material';

import { calculateSecondsToHoursAndMinutes } from '@libs/dateTime';
import {
    TimesheetsNoDataFound,
    MonthCalendar,
    StatusLegend,
    AttendanceSummaryChips,
    MonthlySummaryTable
} from 'timeAndAttendance/components';
import {
    buildMonthDaySheet,
    summarizeDays
} from 'timeAndAttendance/statusTokens';
import { useYearlyTimesheetsState } from './useYearlyTimesheets';

/**
 * My yearly attendance: whole-year summary chips + a wall of 12 mini month
 * calendars (one coloured cell per day, tooltips included), plus the legend.
 * The grid collapses to 2 columns on tablets and 1 on phones.
 */
const Content = () => {
    const { data: user, year } = useYearlyTimesheetsState();

    const monthSheets = useMemo(() => {
        const entries = user?.entries || [];
        const bounds = {
            joiningDate: user?.joiningDate,
            resignationDate: user?.resignationDate
        };
        return Array.from({ length: 12 }, (_, index) => ({
            month: index + 1,
            title: dayjs(`${year}-${index + 1}-01`).format('MMMM'),
            daySheet: buildMonthDaySheet(
                entries,
                `${year}-${index + 1}`,
                bounds
            )
        }));
    }, [user, year]);

    const yearSummary = useMemo(
        () => summarizeDays(monthSheets.flatMap((m) => m.daySheet)),
        [monthSheets]
    );

    if (!user?.email) return <TimesheetsNoDataFound />;

    return (
        <Box sx={{ width: '100%', px: { xs: 0.5, sm: 2 }, pb: 2 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1.5
                }}
            >
                <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    {year}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                    Worked{' '}
                    {calculateSecondsToHoursAndMinutes(
                        user.totalDuration || 0
                    )}
                    h of{' '}
                    {calculateSecondsToHoursAndMinutes(
                        user.totalShiftDuration || 0
                    )}
                    h
                </Typography>
            </Box>

            <AttendanceSummaryChips summary={yearSummary} sx={{ mb: 2 }} />
            <StatusLegend sx={{ mb: 2 }} />

            <Box
                sx={{
                    display: 'grid',
                    gap: { xs: 2.5, sm: 3 },
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(4, 1fr)'
                    }
                }}
            >
                {monthSheets.map(({ month, title, daySheet }) => (
                    <MonthCalendar
                        key={month}
                        compact
                        title={title}
                        daySheet={daySheet}
                        yearMonth={`${year}-${month}`}
                    />
                ))}
            </Box>

            <MonthlySummaryTable
                monthSheets={monthSheets}
                monthlyTotals={user.monthlyTotals}
                sx={{ mt: 4 }}
            />
        </Box>
    );
};

export default Content;
